<template>
  <v-dialog v-model="showDialog" max-width="900" persistent @keydown.esc="close">
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon start color="primary">mdi-truck-delivery</v-icon>
        Livreaza Comanda #{{ order?.id }}
        <v-chip class="ml-2" size="small">{{ order?.good_category_name }}</v-chip>
      </v-card-title>

      <v-card-text>
        <!-- Order Info -->
        <v-alert type="info" variant="tonal" class="mb-4">
          <v-row>
            <v-col cols="12" md="6">
              <div class="text-subtitle-2 mb-1">Client</div>
              <strong>{{ order?.requester_client_name }}</strong><br>
              {{ order?.requester_email }}<br>
              {{ order?.requester_phone }}
            </v-col>
            <v-col cols="12" md="6">
              <div class="text-subtitle-2 mb-1">Comanda</div>
              <strong>{{ order?.quantity }}x {{ order?.product_code_name }}</strong><br>
              <span v-if="order?.network_name">Retea: {{ order?.network_name }}<br></span>
              Total: {{ order?.total_amount }} {{ order?.currency }}
            </v-col>
          </v-row>
          <v-divider class="my-3" />
          <div class="text-subtitle-2 mb-1">Adresa Livrare</div>
          {{ order?.delivery_address_full }}<br>
          <span class="text-caption">
            Tip: {{ order?.delivery_type_name }}
            ({{ order?.delivery_cost }} {{ order?.currency }})
          </span>
        </v-alert>

        <!-- Serial Input -->
        <div class="text-h6 mb-2">
          Seriale {{ order?.good_category_name }}
          <span class="text-caption ml-2">
            ({{ serialsList.length }}/{{ order?.quantity }} introduse)
          </span>
        </div>

        <v-textarea
          v-model="serialsInput"
          label="Seriale (unul pe linie)"
          :placeholder="getSerialPlaceholder()"
          rows="8"
          variant="outlined"
          @input="parseSerials"
          :error="serialsList.length > order?.quantity"
          :error-messages="serialsList.length > order?.quantity ?
            `Prea multe seriale (${serialsList.length} > ${order?.quantity})` : ''"
        />

        <!-- Generate Range -->
        <v-expansion-panels class="mb-4">
          <v-expansion-panel title="Genereaza interval de seriale">
            <v-expansion-panel-text>
              <v-row align="center">
                <v-col>
                  <v-text-field
                    v-model="serialStart"
                    label="De la serial"
                    density="compact"
                    variant="outlined"
                  />
                </v-col>
                <v-col>
                  <v-text-field
                    v-model="serialEnd"
                    label="Pana la serial"
                    density="compact"
                    variant="outlined"
                  />
                </v-col>
                <v-col cols="auto">
                  <v-btn @click="generateRange" color="secondary" variant="tonal">
                    <v-icon start>mdi-auto-fix</v-icon>
                    Genereaza
                  </v-btn>
                </v-col>
              </v-row>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>

        <!-- Validate Button -->
        <v-btn
          @click="validateSerials"
          :loading="validating"
          :disabled="serialsList.length === 0 || serialsList.length !== order?.quantity"
          color="secondary"
          class="mb-4"
        >
          <v-icon start>mdi-check-all</v-icon>
          Verifica Serialele ({{ serialsList.length }})
        </v-btn>

        <!-- Validation Results -->
        <v-alert v-if="validationErrors.length" type="error" class="mb-3">
          <div class="text-subtitle-2 mb-2">
            <v-icon start>mdi-alert-circle</v-icon>
            {{ validationErrors.length }} seriale invalide:
          </div>
          <div
            v-for="err in validationErrors"
            :key="err.serial"
            class="text-caption ml-6"
          >
            <strong>{{ err.serial }}</strong>: {{ err.error }}
          </div>
        </v-alert>

        <v-alert v-if="validatedSerials.length === order?.quantity" type="success" class="mb-3">
          <v-icon start>mdi-check-circle</v-icon>
          Toate {{ validatedSerials.length }} seriale sunt valide si gata de livrare!
        </v-alert>

        <v-alert
          v-else-if="validatedSerials.length > 0 && validatedSerials.length < order?.quantity"
          type="warning"
          class="mb-3"
        >
          <v-icon start>mdi-alert</v-icon>
          {{ validatedSerials.length }} / {{ order?.quantity }} seriale valide
        </v-alert>

        <!-- Fulfillment Notes -->
        <v-textarea
          v-model="fulfillmentNotes"
          label="Note livrare (optional)"
          rows="2"
          variant="outlined"
          placeholder="AWB, observatii, etc."
        />
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="close">Anuleaza</v-btn>
        <v-btn
          color="primary"
          @click="fulfillOrder"
          :loading="fulfilling"
          :disabled="validatedSerials.length !== order?.quantity"
        >
          <v-icon start>mdi-check</v-icon>
          Confirma Livrarea
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { log } from "@/composables/useLog"
import useGetLoggedIn from "@/composables/useGetLoggedIn"
import { validateGoodsSerials, fulfillGoodsOrder } from "@/composables/useGoodsOrdersAPI"

export default {
  name: 'FulfillGoodsOrderDialog',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    order: {
      type: Object,
      default: null
    }
  },
  emits: ['update:modelValue', 'orderFulfilled'],
  data() {
    return {
      showDialog: false,
      validating: false,
      fulfilling: false,
      token: null,

      serialsInput: '',
      serialStart: '',
      serialEnd: '',
      fulfillmentNotes: '',

      validatedSerials: [],
      validationErrors: []
    }
  },
  computed: {
    serialsList() {
      if (!this.serialsInput) return []
      return this.serialsInput
        .split('\n')
        .map(s => s.trim())
        .filter(s => s.length > 0)
    }
  },
  watch: {
    modelValue(val) {
      this.showDialog = val
      if (val) {
        this.initToken()
        this.resetForm()
      }
    },
    showDialog(val) {
      this.$emit('update:modelValue', val)
    }
  },
  methods: {
    async initToken() {
      const { getLoggedIn, token } = useGetLoggedIn()
      await getLoggedIn()
      this.token = token.value
    },
    getSerialPlaceholder() {
      return '89400123456789012345\n89400123456789012346\n89400123456789012347'
    },
    parseSerials() {
      // Reset validation when input changes
      this.validatedSerials = []
      this.validationErrors = []
    },
    generateRange() {
      if (!this.serialStart || !this.serialEnd) {
        alert('Introduceti ambele seriale (start si end)')
        return
      }

      try {
        // Extract numeric parts
        // eslint-disable-next-line no-undef
        const start = BigInt(this.serialStart)
        // eslint-disable-next-line no-undef
        const end = BigInt(this.serialEnd)

        if (end < start) {
          alert('Serialul final trebuie sa fie mai mare decat cel initial')
          return
        }

        const count = Number(end - start) + 1
        if (count > 1000) {
          alert('Intervalul este prea mare (maxim 1000)')
          return
        }

        const serials = []
        for (let i = start; i <= end; i++) {
          serials.push(i.toString())
        }

        this.serialsInput = serials.join('\n')
        this.parseSerials()
      } catch (err) {
        alert('Seriale invalide. Asigurati-va ca sunt numere.')
      }
    },
    async validateSerials() {
      if (!this.token || !this.order?.id) return

      this.validating = true
      this.validatedSerials = []
      this.validationErrors = []

      try {
        const response = await validateGoodsSerials(this.token, this.order.id, this.serialsList)

        if (response.error) {
          alert(response.message)
        } else {
          this.validatedSerials = response.valid || []
          this.validationErrors = response.invalid || []
        }
      } catch (err) {
        log("Error validating serials:", err)
        alert("Eroare la validarea serialelor")
      } finally {
        this.validating = false
      }
    },
    async fulfillOrder() {
      if (!this.token || !this.order?.id) return
      if (this.validatedSerials.length !== this.order.quantity) {
        alert('Nu toate serialele sunt validate')
        return
      }

      this.fulfilling = true

      try {
        const serials = this.validatedSerials.map(v => v.serial)
        const response = await fulfillGoodsOrder(this.token, this.order.id, serials, this.fulfillmentNotes)

        if (response.error) {
          alert(response.message)
        } else {
          alert(response.message || 'Comanda a fost livrata cu succes!')
          this.$emit('orderFulfilled', response)
          this.close()
        }
      } catch (err) {
        log("Error fulfilling order:", err)
        alert("Eroare la livrarea comenzii")
      } finally {
        this.fulfilling = false
      }
    },
    close() {
      this.showDialog = false
      this.resetForm()
    },
    resetForm() {
      this.serialsInput = ''
      this.serialStart = ''
      this.serialEnd = ''
      this.fulfillmentNotes = ''
      this.validatedSerials = []
      this.validationErrors = []
    }
  }
}
</script>
